import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/utils';
import { AccountResponse, AccountType, type CardResponse } from '@shared/api';
import { AccountSelectItem } from './accountSelectItem';
import { CardSelectItems } from './cardSelectItems';
import { Checkbox, ErrorMessage, NoData, SearchInput, Text } from '@shared/widgets';

interface AccountsAndCardsPickerProps {
  className?: string;
  title?: ReactNode;
  hint?: ReactNode;
  accounts: AccountResponse[];
  cards?: CardResponse[];
  onlyPaymentAccounts?: boolean;
  noCards?: boolean;
  noAccounts?: boolean;
}

export const AccountsAndCardsPicker = (props: AccountsAndCardsPickerProps) => {
  const [search, setSearch] = useState<string>('');
  const { control, clearErrors, setValue, watch } = useFormContext<{ accounts: number[]; cards?: CardResponse[] }>();
  const [selectedAccounts, selectedCards] = watch(['accounts', 'cards']);
  const cards = selectedCards ?? [];

  const { t } = useTranslation('userManagement');

  useEffect(() => {
    const hasSelection = props.noAccounts
      ? cards.length > 0
      : props.noCards
        ? selectedAccounts.length > 0
        : selectedAccounts.length > 0 || cards.length > 0;
    if (hasSelection) clearErrors('accounts');
  }, [clearErrors, props.noAccounts, props.noCards, selectedAccounts.length, cards.length]);

  const selectedCardIds = useMemo(() => new Set(cards.map((card) => card.cardId)), [cards]);

  const accountCardsMap = useMemo(
    () =>
      props.cards?.reduce<Record<number, CardResponse[]>>((acc, card) => {
        acc[card.accountId] ??= [];
        acc[card.accountId].push(card);
        return acc;
      }, {}),
    [props.cards],
  );
  const sortedAccounts = useMemo(
    () =>
      [...props.accounts].sort((a, b) => {
        if (a.type === b.type) return 0;
        if (a.type === AccountType.Payment) return -1;
        if (b.type === AccountType.Payment) return 1;
        return 0;
      }),
    [props.accounts],
  );
  const selectableAccounts = useMemo(
    () => (props.onlyPaymentAccounts ? sortedAccounts.filter((account) => account.type === AccountType.Payment) : sortedAccounts),
    [sortedAccounts, props.onlyPaymentAccounts],
  );
  const selectableAccountIds = useMemo(() => selectableAccounts.map((account) => account.id), [selectableAccounts]);
  const allCards = useMemo(() => props.cards ?? [], [props.cards]);
  const allNonPaymentMainCards = useMemo(
    () => sortedAccounts.filter((account) => account.type !== AccountType.Payment).flatMap((account) => accountCardsMap?.[account.id] ?? []),
    [sortedAccounts, accountCardsMap],
  );
  const isAllSelected = props.noAccounts
    ? allCards.every((card) => selectedCardIds.has(card.cardId))
    : props.noCards
      ? selectableAccountIds.every((accountId) => selectedAccounts.includes(accountId))
      : selectableAccountIds.every((accountId) => selectedAccounts.includes(accountId)) &&
        allNonPaymentMainCards.every((card) => selectedCardIds.has(card.cardId));

  const filteredAccounts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const hasLettersInQuery = /[a-z]/i.test(normalizedSearch);
    const useDigitsSearch = !hasLettersInQuery;
    const normalizedSearchDigits = normalizedSearch.replace(/\D/g, '');

    return sortedAccounts.filter((account) => {
      if (!normalizedSearch) return true;
      const accountNumber = String(account.accountNumber ?? '').toLowerCase();
      const accountNumberDigits = accountNumber.replace(/\D/g, '');
      const accountMatches =
        accountNumber.includes(normalizedSearch) ||
        (useDigitsSearch && normalizedSearchDigits.length > 0 && accountNumberDigits.includes(normalizedSearchDigits));
      if (accountMatches) return true;

      const accountCards = accountCardsMap?.[account.id] ?? [];
      return accountCards.some((card) => {
        const cardNumber = String(card.cardNumber ?? '').toLowerCase();
        const cardNumberDigits = cardNumber.replace(/\D/g, '');
        return (
          cardNumber.includes(normalizedSearch) ||
          (useDigitsSearch && normalizedSearchDigits.length > 0 && cardNumberDigits.includes(normalizedSearchDigits))
        );
      });
    });
  }, [sortedAccounts, accountCardsMap, search]);

  return (
    <section className={cn('flex flex-col gap-4', 'max-md:gap-2', props.className)}>
      {props.title && <Text>{props.title}</Text>}
      <div className={cn('flex items-center justify-between gap-4', 'max-md:gap-2')}>
        <Checkbox
          className='shrink-0'
          label='Select all'
          value={isAllSelected}
          onChange={(value) => {
            if (!value) {
              if (!props.noAccounts) setValue('accounts', []);
              if (!props.noCards) setValue('cards', []);
              return;
            }

            if (!props.noAccounts) setValue('accounts', selectableAccountIds);
            if (!props.noCards) setValue('cards', props.noAccounts ? allCards : allNonPaymentMainCards);
          }}
        />
        <SearchInput className='w-full' value={search} onChange={setSearch} />
      </div>
      <Controller
        name='accounts'
        control={control}
        rules={{
          validate: (value) => {
            if (props.noAccounts) return cards.length > 0 || t('modals.add.accountsOrCardsRequired');
            if (props.noCards) return value.length > 0 || t('modals.add.accountsOrCardsRequired');
            return value.length > 0 || cards.length > 0 || t('modals.add.accountsOrCardsRequired');
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <div>
            <div className='flex max-h-[300px] flex-col gap-2 overflow-auto'>
              {filteredAccounts.length === 0 && <NoData className='py-4' />}
              {filteredAccounts.map((account) => (
                <AccountSelectItem
                  key={account.id}
                  account={account}
                  onlyPaymentAccounts={props.onlyPaymentAccounts}
                  hideCheckbox={props.noAccounts}
                  value={value.includes(account.id)}
                  onChange={(val) => {
                    if (!props.noCards) {
                      setValue(
                        'cards',
                        cards.filter((card) => card.accountId !== account.id),
                      );
                    }
                    onChange(val ? [...value, account.id] : value.filter((id) => id !== account.id));
                  }}
                >
                  {!props.noCards && <CardSelectItems cards={accountCardsMap?.[account.id] ?? []} allSelected={value.includes(account.id)} />}
                </AccountSelectItem>
              ))}
            </div>
            {error?.message && <ErrorMessage text={error.message} />}
          </div>
        )}
      />
      {props.hint && (
        <Text weight='light' size='xs' color='secondary'>
          {props.hint}
        </Text>
      )}
    </section>
  );
};
